import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import AutoAwesomeMotionRoundedIcon from "@mui/icons-material/AutoAwesomeMotionRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Logo from "../UI/Logo";
import NotificationIcon from "./NotificationIcon";

type MobileNavigationProps = {
  title: string | null;
  themeMode: "light" | "dark";
  onToggleTheme: () => void;
  onLogout: () => void;
};



export default function MobileNavigation({
  title,
  themeMode,
  onToggleTheme,
  onLogout,
}: MobileNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const activeSection = location.pathname.startsWith("/dashboard")
    ? "/dashboard"
    : location.pathname.startsWith("/environmental-feed")
      ? "/environmental-feed"
      : false;

  const goTo = (path: string) => {
    setMenuOpen(false);
    const shouldPreserveSearch = path !== "/environmental-feed";
    const destination = shouldPreserveSearch && location.search && !path.includes("?")
      ? `${path}${location.search}`
      : path;
    navigate(destination);
  };

  const glassSurface = {
    border: "1px solid",
    borderColor: (theme: Theme) =>
      theme.palette.mode === "dark" ? "rgba(166, 232, 210, 0.18)" : "rgba(22, 105, 81, 0.14)",
    backgroundColor: (theme: Theme) =>
      theme.palette.mode === "dark" ? "rgba(16, 45, 54, 0.58)" : "rgba(252, 254, 253, 0.68)",
    backdropFilter: "blur(16px) saturate(125%)",
    boxShadow: (theme: Theme) =>
      theme.palette.mode === "dark"
        ? "0 10px 28px rgba(3, 15, 27, 0.28)"
        : "0 10px 28px rgba(18, 70, 61, 0.14)",
  };

  const alternateView = activeSection === "/dashboard"
    ? { label: "Ver novedades", path: "/environmental-feed", icon: <AutoAwesomeMotionRoundedIcon /> }
    : activeSection === "/environmental-feed"
      ? { label: "Ver mapa", path: "/dashboard", icon: <ExploreRoundedIcon /> }
      : null;
  const followedOnly = activeSection === "/dashboard"
    && new URLSearchParams(location.search).get("favorites") === "1";
  const toggleFollowedPlaces = () => {
    setMenuOpen(false);
    navigate(followedOnly ? "/dashboard" : "/dashboard?favorites=1");
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Box
        component="header"
        sx={{
          position: "fixed",
          zIndex: (theme) => theme.zIndex.appBar,
          top: 0,
          left: 0,
          right: 0,
          px: 1.5,
          pt: "calc(12px + env(safe-area-inset-top))",
          pointerEvents: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              ...glassSurface,
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              gap: 1,
              minHeight: 44,
              px: 1.5,
              borderRadius: 99,
            }}
          >
            <Logo mobile />
            <Typography
              component="h1"
              variant="caption"
              noWrap
              sx={{ fontWeight: 800, letterSpacing: "0.02em", color: "text.primary" }}
            >
              {title === "Observatorio Marino/Costero" ? "Observatorio" : title}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto", pointerEvents: "auto" }}>
            <Box
              sx={{
                ...glassSurface,
                width: 42,
                height: 42,
                backdropFilter: "none",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NotificationIcon />
            </Box>
            <Box
              sx={{
                ...glassSurface,
                backgroundColor: "var(--proteo-map-control-surface)",
                width: 42,
                height: 42,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconButton
                color="inherit"
                aria-label={themeMode === "light" ? "Activar tema oscuro" : "Activar tema claro"}
                onClick={onToggleTheme}
              >
                {themeMode === "light" ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}
              </IconButton>
            </Box>
            <Box
              sx={{
                ...glassSurface,
                backgroundColor: "var(--proteo-map-control-surface)",
                width: 42,
                height: 42,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconButton color="inherit" aria-label="Abrir menú" onClick={() => setMenuOpen(true)}>
                <MenuRoundedIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          overflowX: "hidden",
          paddingTop: "calc(16px + env(safe-area-inset-top))",
          paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
        }}
      >
        <Outlet />
      </Box>

      {alternateView && (
        <Button
          startIcon={alternateView.icon}
          onClick={() => goTo(alternateView.path)}
          sx={{
            ...glassSurface,
            backgroundColor: "var(--proteo-map-control-surface)",
            position: "fixed",
            zIndex: (theme) => theme.zIndex.appBar,
            left: 16,
            bottom: "calc(44px + env(safe-area-inset-bottom))",
            minHeight: 46,
            px: 2,
            borderRadius: 99,
            color: "text.primary",
            textTransform: "none",
            fontWeight: 800,
          }}
        >
          {alternateView.label}
        </Button>
      )}

      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        PaperProps={{
          sx: {
            color: themeMode === "light" ? "#e7fffb" : "text.primary",
            "& .MuiListItemText-primary": {
              color: themeMode === "light" ? "#e7fffb" : "text.primary",
            },
            "& .MuiListItemIcon-root": {
              color: themeMode === "light" ? "#a6e8d2" : "text.secondary",
            },
            "& .MuiListItemButton-root:hover": {
              backgroundColor: "rgba(166, 232, 210, 0.14)",
            },
          },
        }}
      >
        <Box sx={{ width: "min(84vw, 320px)", paddingTop: "env(safe-area-inset-top)" }} role="presentation">
          <List aria-label="Configuración y cuenta">
            <ListItemButton onClick={onToggleTheme}>
              <ListItemIcon>{themeMode === "light" ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}</ListItemIcon>
              <ListItemText primary={themeMode === "light" ? "Tema oscuro" : "Tema claro"} />
            </ListItemButton>
            <ListItemButton onClick={() => goTo("/environmental-feed?saved=1")}>
              <ListItemIcon><BookmarkRoundedIcon /></ListItemIcon>
              <ListItemText primary="Señales guardadas" />
            </ListItemButton>
            <ListItemButton
              onClick={toggleFollowedPlaces}
              aria-pressed={followedOnly}
            >
              <ListItemIcon><BookmarkRoundedIcon /></ListItemIcon>
              <ListItemText primary={followedOnly ? "Todas las estaciones" : "Estaciones seguidas"} />
            </ListItemButton>
            <ListItemButton onClick={onLogout}>
              <ListItemIcon><LogoutRoundedIcon /></ListItemIcon>
              <ListItemText primary="Cerrar sesión" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
